export type ContactAgentDomain = 'COBRANZAS' | 'HELPDESK' | 'LEADS';
export type ContactMode = 'A' | 'B' | 'C';
export type CampaignPurpose = 'COBRANZAS' | 'AVISO';
export type CampaignDeliveryMode = 'CONVERSATIONAL' | 'NOTIFICATION';
export type CampaignChannelKind = 'WHATSAPP' | 'SMS' | 'EMAIL';
export type MessageTemplateStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
export type NoticeSendPolicy = 'IMMEDIATE' | 'CAMPAIGN_WINDOW';
export type NoticeDispatchStatus =
  | 'QUEUED'
  | 'EXCLUDED'
  | 'DEFERRED'
  | 'SENDING'
  | 'SENT'
  | 'RESPONDED'
  | 'FAILED';
export type NoticeDispatchReason =
  | 'MISSING_EMAIL'
  | 'INVALID_PHONE'
  | 'OPT_OUT'
  | 'CHANNEL_OPT_OUT'
  | 'FREQUENCY_CAP'
  | 'PROGRAM_PAUSED'
  | 'CONTACTABILITY_REQUIRED'
  | 'OUTSIDE_WINDOW';
export type CampaignTemplateVariableSource =
  | 'contact.firstName'
  | 'contact.lastName'
  | 'contact.businessName'
  | 'contact.phoneE164'
  | 'contact.email'
  | 'campaign.accessUrl'
  | 'debt.invoiceNumber'
  | 'debt.balance'
  | 'debt.dueDate'
  | 'shipment.tracking'
  | 'shipment.eta'
  | 'shipment.address'
  | 'invoice.number'
  | 'invoice.amount';
/** Maps Meta placeholder index ("1", "2", …) to a data source */
export type CampaignTemplateVariableMapping = Record<string, CampaignTemplateVariableSource>;
/** Per-template mappings keyed by template name */
export type CampaignTemplateVariableConfig = Record<string, CampaignTemplateVariableMapping>;
export type CampaignScheduleKind = 'URBANO' | 'RURAL';
export type CampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED';
export type CampaignAudienceMode = 'SEGMENT' | 'EXPLICIT';
export type CampaignAudienceSource = 'MANUAL' | 'IMPORT' | 'SEGMENT' | 'INTEGRATION';
export type CampaignAudienceStatus =
  | 'PENDING'
  | 'DEFERRED'
  | 'QUEUED'
  | 'SENDING'
  | 'SENT'
  | 'RESPONDED'
  | 'OPTED_OUT'
  | 'EXCLUDED'
  | 'FAILED';
export type CampaignExclusionReason =
  | 'OPT_OUT'
  | 'FREQUENCY_CAP'
  | 'ACTIVE_CONVERSATION'
  | 'INVALID_PHONE'
  | 'MISSING_DEBT'
  | 'MISSING_TEMPLATE_DATA'
  | 'DUPLICATE'
  | 'NOT_AUTHORIZED'
  | 'CONTACTABILITY_REQUIRED'
  | 'PROVIDER_REJECTED'
  | 'MISSING_EMAIL'
  | 'CHANNEL_OPT_OUT'
  | 'CHANNEL_DISABLED'
  | 'PROGRAM_PAUSED'
  | 'CAMPAIGN_ENDED';
export type CampaignCreatorType = 'USER' | 'INTEGRATION';
export type CampaignBoardKey = 'draft' | 'started' | 'paused' | 'resumed' | 'stopped';
export const EXCLUSION_REASON_LABEL: Record<CampaignExclusionReason, string> = {
  OPT_OUT: 'El contacto pidio no ser contactado',
  FREQUENCY_CAP: 'Supera el tope de mensajes recientes',
  ACTIVE_CONVERSATION: 'Ya tiene una conversacion activa',
  INVALID_PHONE: 'Telefono invalido',
  MISSING_DEBT: 'No hay deuda principal valida',
  MISSING_TEMPLATE_DATA: 'Faltan datos para la plantilla',
  DUPLICATE: 'Contacto duplicado en la campaña',
  NOT_AUTHORIZED: 'No autorizado para este tenant',
  CONTACTABILITY_REQUIRED: 'Falta politica o consentimiento de contactabilidad',
  PROVIDER_REJECTED: 'El proveedor rechazo el envio',
  MISSING_EMAIL: 'Falta el email',
  CHANNEL_OPT_OUT: 'Baja de este canal',
  CHANNEL_DISABLED: 'El canal no esta habilitado',
  PROGRAM_PAUSED: 'El programa esta pausado',
  CAMPAIGN_ENDED: 'Campaña finalizada',
};

export const DELIVERY_MODE_LABEL: Record<CampaignDeliveryMode, string> = {
  CONVERSATIONAL: 'Conversacional',
  NOTIFICATION: 'Notificacion',
};

export const CHANNEL_KIND_LABEL: Record<CampaignChannelKind, string> = {
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  EMAIL: 'Email',
};

export const PURPOSE_LABEL: Record<CampaignPurpose, string> = {
  COBRANZAS: 'Cobranzas',
  AVISO: 'Aviso',
};

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activa',
  PAUSED: 'Pausada',
  COMPLETED: 'Completada',
};

export const NOTICE_SEND_POLICY_LABEL: Record<NoticeSendPolicy, string> = {
  IMMEDIATE: 'Enviar ahora',
  CAMPAIGN_WINDOW: 'Respetar ventana 9 a 18',
};

export const MESSAGE_TEMPLATE_STATUS_LABEL: Record<MessageTemplateStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activa',
  ARCHIVED: 'Archivada',
};

/** Limites del editor SMS/email. 1600 cubre 10 SMS concatenados; el HTML es tipografia de sistema, no CMS. */
export const CAMPAIGN_MESSAGE_TEMPLATE_LIMITS = {
  smsSegmentChars: 160,
  smsBodyMax: 1600,
  emailSubjectMax: 255,
  emailBodyMax: 8000,
  nameMax: 255,
  /** Tope de WhatsApp para texto libre; SMS se recorta a smsBodyMax al enviar. */
  autoReplyMax: 4096,
} as const;

export const DEFAULT_NOTIFICATION_AUTO_REPLY = [
  'Hola. Recibimos tu mensaje.',
  '',
  'Esta línea es de uso informativo y las respuestas son automáticas. No se atienden consultas ni reclamos por este medio.',
  '',
  'Para consultar tu estado de cuenta, informar un pago ya realizado o solicitar un plan de pagos, comunicate con la Comuna:',
  '',
  '📞 [teléfono]',
  '🕐 [días y horario de atención]',
  '📍 [dirección]',
  '',
  'Si no querés recibir más avisos, respondé BAJA.',
].join('\n');

export type MessagingChannelKind = 'SMS' | 'EMAIL';
export type MessagingProvider = 'TWILIO_SMS' | 'TWILIO_SENDGRID';
export type CampaignChannelCredentialStatus = 'configured' | 'inherited' | 'missing';

export const CHANNEL_CREDENTIAL_STATUS_LABEL: Record<CampaignChannelCredentialStatus, string> = {
  configured: 'Configurado en el bot',
  inherited: 'Heredado de la instancia',
  missing: 'Falta configurar',
};

export interface MessagingChannelPublic {
  id: string;
  tenantId: string;
  instanceId: string;
  instanceBotId: string | null;
  kind: MessagingChannelKind;
  purpose: 'CONTACTO';
  provider: MessagingProvider;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ERROR';
  fromAddress: string;
  accountSid: string | null;
  messagingServiceSid: string | null;
  hasAuthToken: boolean;
  hasSendgridKey: boolean;
  emailAccountId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMessagingChannelRequest {
  instanceId: string;
  instanceBotId?: string | null;
  kind: MessagingChannelKind;
  fromAddress: string;
  accountSid?: string;
  authToken?: string;
  messagingServiceSid?: string;
  sendgridApiKey?: string;
  emailAccountId?: string | null;
}

export interface UpdateMessagingChannelRequest {
  fromAddress?: string;
  accountSid?: string;
  authToken?: string;
  messagingServiceSid?: string;
  sendgridApiKey?: string;
  emailAccountId?: string | null;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ERROR';
}

export interface CampaignTestSendRequest {
  to: string;
  channelKind?: CampaignChannelKind;
  templateId?: string;
  messageTemplateId?: string;
  variables?: Record<string, string>;
  openConversation?: boolean;
  markAsTest?: boolean;
  subjectOverride?: string;
}

export interface CampaignTestSendResult {
  queued: boolean;
  conversationId?: string;
  source?: 'BOT' | 'INSTANCE';
}

export interface CampaignTestSendPreviewRequest {
  channelKind?: CampaignChannelKind;
  templateId?: string;
  messageTemplateId?: string;
  markAsTest?: boolean;
}

export interface CampaignTestSendPreviewResult {
  subject?: string;
  bodyText: string;
  bodyHtml?: string;
}

export interface MessageTemplatePreviewRequest {
  channelKind: 'SMS' | 'EMAIL';
  subject?: string;
  bodyText: string;
  bodyHtml?: string;
}

export interface CampaignTestConversation {
  id: string;
  startedAt: string;
  phoneMasked: string;
  fsmState: string | null;
  status: string;
}
export type ContactExceptionStatus = 'OPEN' | 'TAKEN' | 'CLOSED';
export type PaymentStatus = 'DECLARED' | 'RECEIPT' | 'PROBABLE' | 'CONFIRMED' | 'REJECTED';

// Propositos de mensajes proactivos que, fuera de la ventana de 24h de WhatsApp, deben
// enviarse por plantilla aprobada por Meta.
export type ProactiveTemplatePurpose =
  | 'PROMISE_KEPT_SETTLED'
  | 'PROMISE_KEPT_WITH_BALANCE'
  | 'PROMISE_EXPIRED'
  | 'WINDOW_NUDGE';

export interface ProactiveTemplateMapEntry {
  id: string;
  channelId: string;
  purpose: ProactiveTemplatePurpose;
  templateId: string;
  templateName: string;
  templateStatus: string;
}

export type PortfolioPluginKind = 'EXCEL' | 'SETUP_MSSQL';
export type PortfolioSyncStatus = 'IDLE' | 'RUNNING' | 'SUCCESS' | 'FAILED';

export type SetupEventDetectorId =
  | 'SHIPMENT_DELIVERED'
  | 'SHIPMENT_DISPATCHED'
  | 'SHIPMENT_ARRIVING'
  | 'INVOICE_ISSUED'
  | 'PAYMENT_RECEIVED';

export const SETUP_EVENT_DETECTOR_IDS: readonly SetupEventDetectorId[] = [
  'SHIPMENT_DELIVERED',
  'SHIPMENT_DISPATCHED',
  'SHIPMENT_ARRIVING',
  'INVOICE_ISSUED',
  'PAYMENT_RECEIVED',
];

export interface EventDetectorManifest {
  id: SetupEventDetectorId;
  label: string;
  description: string;
  defaultCadenceMinutes: number;
  variableKeys: string[];
}

export const SETUP_EVENT_DETECTORS: EventDetectorManifest[] = [
  {
    id: 'SHIPMENT_DELIVERED',
    label: 'Llegada de paquetería',
    description: 'El destinatario se entera cuando el paquete llegó, no cuando llama.',
    defaultCadenceMinutes: 2,
    variableKeys: ['shipment.tracking', 'shipment.address', 'shipment.eta'],
  },
  {
    id: 'SHIPMENT_DISPATCHED',
    label: 'Paquete despachado',
    description: 'Corta el “¿ya salió?” con el alta o la salida del parte.',
    defaultCadenceMinutes: 2,
    variableKeys: ['shipment.tracking', 'shipment.address', 'shipment.eta'],
  },
  {
    id: 'SHIPMENT_ARRIVING',
    label: 'Paquete llega hoy',
    description: 'Avisa el día de la entrega, con ETA y destino.',
    defaultCadenceMinutes: 15,
    variableKeys: ['shipment.tracking', 'shipment.eta', 'shipment.address'],
  },
  {
    id: 'INVOICE_ISSUED',
    label: 'Factura emitida',
    description: 'El comprobante nuevo llega al cliente sin esperar el correo.',
    defaultCadenceMinutes: 5,
    variableKeys: ['invoice.number', 'invoice.amount'],
  },
  {
    id: 'PAYMENT_RECEIVED',
    label: 'Pago acreditado',
    description: 'Un gracias automático cuando entra el haber.',
    defaultCadenceMinutes: 5,
    variableKeys: ['invoice.number', 'invoice.amount'],
  },
];

export interface PortfolioPluginManifest {
  kind: PortfolioPluginKind;
  label: string;
  description: string;
  capabilities: {
    syncPortfolio: boolean;
    livePaymentStatus: boolean;
    sweepEvents: boolean;
  };
}

export interface PortfolioConnectionPublic {
  id: string;
  tenantId: string;
  pluginKind: PortfolioPluginKind;
  name: string;
  host: string | null;
  port: number | null;
  database: string | null;
  username: string | null;
  hasPassword: boolean;
  options: Record<string, unknown> | null;
  lastSyncAt: string | null;
  lastSyncStatus: PortfolioSyncStatus;
  lastSyncError: string | null;
  lastSyncStats: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePortfolioConnectionRequest {
  pluginKind: PortfolioPluginKind;
  name: string;
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password?: string;
  options?: Record<string, unknown>;
}

export interface UpdatePortfolioConnectionRequest {
  name?: string;
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password?: string;
  options?: Record<string, unknown>;
}

/** Reglas de negociacion del gestor de cobranzas. Todo es opcional: hay defaults seguros. */
export interface ContactAgentCollectionRules {
  /** Zona horaria con la que se calcula "hoy" y los vencimientos. */
  timezone?: string;
  /** MONTH_END: se agenda hasta fin de mes (con un piso de dias). DAYS: N dias desde hoy. */
  promiseDeadline?: 'MONTH_END' | 'DAYS';
  promiseMaxDays?: number;
  promiseMinDays?: number;
  /** Turnos sin acordar fecha antes de pasar el caso a una persona. */
  maxNegotiationAttempts?: number;
}

export interface ContactAgentEndRules {
  paymentMethodsText?: string;
  collection?: ContactAgentCollectionRules;
}

export interface ContactAgent {
  id: string;
  tenantId: string;
  botId: string;
  domain: ContactAgentDomain;
  name: string;
  objective: string | null;
  toolsEnabled: string[];
  endRules: ContactAgentEndRules | null;
  sourceConnectionId: string | null;
  createdAt: string;
  updatedAt: string;
  bot?: { id: string; name: string; systemPrompt?: string | null };
  sourceConnection?: PortfolioConnectionPublic | null;
}

export interface ContactAgentAvailableBot {
  botId: string;
  name: string;
  systemPrompt: string;
  identificationEligible: boolean;
  identificationMethods: string[];
  identificationIssues: string[];
}

export interface ContactRecord {
  id: string;
  tenantId: string;
  phoneE164: string | null;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
  email: string | null;
  extra: Record<string, unknown> | null;
  optOut: boolean;
  optOutReason: string | null;
  optOutAt: string | null;
  createdAt: string;
  updatedAt: string;
  debts?: DebtRecord[];
}

export interface DebtRecord {
  id: string;
  tenantId: string;
  contactId: string;
  invoiceNumber: string;
  amount: string;
  currency: string;
  dueDate: string;
  balance: string;
  seller: string | null;
  branch: string | null;
  importId: string | null;
  sourceKey: string;
  externalRef: string | null;
}

export interface PortfolioImportResult {
  importId: string;
  rowCount: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  errors: string[];
}

export interface PortfolioImportMapping {
  firstName?: string;
  lastName?: string;
  businessName?: string;
  phone?: string;
  email?: string;
  invoiceNumber: string;
  amount: string;
  dueDate: string;
  balance?: string;
  seller?: string;
  branch?: string;
}

export interface SegmentFilter {
  overdueOnly?: boolean;
  minBalance?: number;
  minTotalBalance?: number;
  maxTotalBalance?: number;
  minInvoiceCount?: number;
  maxInvoiceCount?: number;
  branch?: string;
  seller?: string;
  excludedContactIds?: string[];
}

export interface FrozenMessage {
  subject?: string;
  bodyText: string;
  bodyHtml?: string;
  variableKeys: string[];
  /** Solo plantillas de WhatsApp: idioma con el que Meta la aprobo (ej. "es_AR"). */
  language?: string;
}

export interface CampaignRecord {
  id: string;
  tenantId: string;
  agentId: string | null;
  whatsAppChannelId: string | null;
  messagingChannelId: string | null;
  messageTemplateId: string | null;
  frozenMessage: FrozenMessage | null;
  autoReplyText: string | null;
  name: string;
  purpose: CampaignPurpose;
  deliveryMode: CampaignDeliveryMode;
  channelKind: CampaignChannelKind;
  segmentFilter: SegmentFilter | null;
  businessDays: number[];
  scheduleFrom: string;
  scheduleTo: string;
  scheduleKind: CampaignScheduleKind;
  afternoonFrom: string | null;
  afternoonTo: string | null;
  saturdayFrom: string | null;
  saturdayTo: string | null;
  saturdayAfternoonFrom: string | null;
  saturdayAfternoonTo: string | null;
  sundayFrom: string | null;
  sundayTo: string | null;
  sundayAfternoonFrom: string | null;
  sundayAfternoonTo: string | null;
  timezone: string;
  holidays: string[];
  blackout: Record<string, unknown> | null;
  initialTemplateId: string | null;
  templateVariableMap: CampaignTemplateVariableConfig | null;
  retryRules: Record<string, unknown> | null;
  endCondition: Record<string, unknown> | null;
  status: CampaignStatus;
  maxContactsPerDay: number;
  audienceMode: CampaignAudienceMode;
  audienceRevision: number;
  audienceFrozenAt: string | null;
  audienceHash: string | null;
  externalRef: string | null;
  createdByType: CampaignCreatorType;
  createdByExternalSubject: string | null;
  startedAt: string | null;
  pausedAt: string | null;
  resumedAt: string | null;
  stoppedAt: string | null;
  endsAt: string;
  createdAt: string;
  updatedAt: string;
  agent?: { id: string; name: string };
  steps?: CampaignStepRecord[];
  conversationsStarted?: number;
  conversationsResponded?: number;
}

export interface CampaignStepRecord {
  id: string;
  campaignId: string;
  stepOrder: number;
  waitHours: number;
  templateId: string;
  condition: Record<string, unknown> | null;
}

export interface ContactExceptionRecord {
  id: string;
  tenantId: string;
  contactId: string;
  conversationId: string | null;
  reason: string;
  aiSummary: string | null;
  documentRefs: string[];
  recommendedAction: string | null;
  assignedTo: number | null;
  status: ContactExceptionStatus;
  createdAt: string;
  updatedAt: string;
  contact?: {
    phoneE164: string | null;
    firstName: string | null;
    businessName: string | null;
  };
  conversation?: {
    handoffStatus?: string | null;
    windowExpiresAt?: string | null;
    fsmState?: string | null;
    pendingReason?: string | null;
    messages?: ContactExceptionMessage[];
  } | null;
}

export interface ContactExceptionMessage {
  id: string;
  sender: string;
  content: string;
  contentType: string;
  fileUrl: string | null;
  contextContent: string | null;
  createdAt: string;
}

export interface ContactExceptionPayment {
  id: string;
  status: PaymentStatus;
  amount: string;
  evidenceRef: string | null;
  createdAt: string;
  debt?: { invoiceNumber: string; balance: string };
}

/** Resumen de un compromiso de pago para vistas de seguimiento (desk y ficha de contacto). */
export interface ContactPromiseSummary {
  id: string;
  amount: string;
  promisedDate: string;
  status: string;
  createdAt: string;
  invoiceNumber: string;
  currency: string;
}

export interface ContactExceptionDesk {
  exception: ContactExceptionRecord;
  payments: ContactExceptionPayment[];
  debts: Array<{
    id: string;
    invoiceNumber: string;
    balance: string;
    dueDate: string;
    currency: string;
  }>;
  promises: ContactPromiseSummary[];
}

export interface ContactTrackingConversation {
  id: string;
  channel: string;
  fsmState: string | null;
  pendingReason: string | null;
  handoffStatus: string;
  startedAt: string;
  lastMessageAt: string;
  messageCount: number;
  campaignName: string | null;
}

export interface ContactTrackingAudit {
  id: string;
  actor: string;
  action: string;
  template: string | null;
  detail: Record<string, unknown> | null;
  createdAt: string;
}

export interface ContactTrackingException {
  id: string;
  status: ContactExceptionStatus;
  reason: string;
  createdAt: string;
}

/** Hilo de una conversacion visto desde la ficha del contacto. */
export interface ContactConversationThread {
  conversationId: string;
  channel: string;
  handoffStatus: string;
  fsmState: string | null;
  pendingReason: string | null;
  windowExpiresAt: string | null;
  /** Caso abierto (OPEN/TAKEN) del contacto, si existe: habilita las acciones de operador. */
  openExceptionId: string | null;
  messages: ContactExceptionMessage[];
}

/** Ficha de seguimiento de una deuda: todo el historial del contacto en una sola vista. */
export interface ContactTrackingResult {
  contact: ContactRecord;
  promises: ContactPromiseSummary[];
  conversations: ContactTrackingConversation[];
  audits: ContactTrackingAudit[];
  exceptions: ContactTrackingException[];
  payments: ContactExceptionPayment[];
}

export type WorkQueueKind = 'PAYMENT_REVIEW' | 'EXCEPTION' | 'EXPIRED_PROMISE' | 'WAITING_REPLY';

/** Un caso que requiere accion humana en la bandeja de trabajo del operador. */
export interface WorkQueueItem {
  /** Sintetico: `${kind}:${refId}` para key estable en UI. */
  id: string;
  kind: WorkQueueKind;
  contactId: string;
  contactLabel: string;
  phoneE164: string | null;
  amount: string | null;
  currency: string | null;
  reason: string;
  /** Desde cuando espera accion (ISO). */
  since: string;
  exceptionId: string | null;
  conversationId: string | null;
}

export interface WorkQueueResult {
  items: WorkQueueItem[];
  counts: Record<WorkQueueKind, number>;
}

export type PaymentPromiseStatus = 'ACTIVE' | 'KEPT' | 'EXPIRED' | 'REPLACED';

export type PaymentPromiseOrigin = 'SELF_SERVICE' | 'CAMPAIGN';

export interface PaymentPromiseRecord {
  id: string;
  contactId: string;
  debtId: string;
  amount: string;
  currency: string;
  promisedDate: string;
  status: PaymentPromiseStatus;
  createdAt: string;
  invoiceNumber: string;
  origin: PaymentPromiseOrigin;
  campaignName: string | null;
  channel: string | null;
  contact: {
    firstName: string | null;
    lastName: string | null;
    businessName: string | null;
    phoneE164: string | null;
  };
}

export interface PaymentPromiseListResult {
  items: PaymentPromiseRecord[];
  total: number;
}

export interface ContactAgentDashboardMetrics {
  draft: number;
  started: number;
  paused: number;
  resumed: number;
  stopped: number;
  conversationsStarted: number;
  conversationsResponded: number;
  conversationsNoResponse: number;
}

export function campaignBoardKey(
  status: CampaignStatus | string,
  resumedAt: string | Date | null,
): CampaignBoardKey {
  if (status === 'DRAFT') {
    return 'draft';
  }
  if (status === 'PAUSED') {
    return 'paused';
  }
  if (status === 'COMPLETED') {
    return 'stopped';
  }
  if (status === 'ACTIVE' && resumedAt) {
    return 'resumed';
  }
  return 'started';
}

export function summarizeCampaignBoard(
  campaigns: Array<{
    status: CampaignStatus | string;
    resumedAt: string | Date | null;
    conversationsStarted?: number;
    conversationsResponded?: number;
  }>,
): ContactAgentDashboardMetrics {
  const board: ContactAgentDashboardMetrics = {
    draft: 0,
    started: 0,
    paused: 0,
    resumed: 0,
    stopped: 0,
    conversationsStarted: 0,
    conversationsResponded: 0,
    conversationsNoResponse: 0,
  };
  for (const campaign of campaigns) {
    board[campaignBoardKey(campaign.status, campaign.resumedAt)] += 1;
    board.conversationsStarted += campaign.conversationsStarted ?? 0;
    board.conversationsResponded += campaign.conversationsResponded ?? 0;
  }
  board.conversationsNoResponse = Math.max(
    board.conversationsStarted - board.conversationsResponded,
    0,
  );
  return board;
}

export interface CampaignCobranzasMetrics {
  portfolioLoaded: number;
  overdueAmount: string;
  recoveredAmount: string;
  recoveryRate: number;
  promisesActive: number;
  promisedAmount: string;
}

export interface CampaignDetailMetrics {
  audienceCount: number;
  conversationsStarted: number;
  conversationsResponded: number;
  conversationsNoResponse: number;
  optOut: number;
  handedOff: number;
  paid: number;
  exhausted: number;
  cobranzas: CampaignCobranzasMetrics | null;
}

export interface CreateContactAgentRequest {
  botId: string;
  domain: ContactAgentDomain;
  name: string;
  objective?: string;
  toolsEnabled?: string[];
  paymentMethodsText?: string;
  collectionRules?: ContactAgentCollectionRules;
  systemPrompt?: string;
  sourceConnectionId?: string | null;
}

export interface UpdateContactAgentRequest {
  name?: string;
  objective?: string;
  toolsEnabled?: string[];
  paymentMethodsText?: string;
  collectionRules?: ContactAgentCollectionRules;
  systemPrompt?: string;
  sourceConnectionId?: string | null;
}

export interface CampaignAudienceMemberRecord {
  id: string;
  tenantId: string;
  campaignId: string;
  contactId: string;
  debtId: string | null;
  source: CampaignAudienceSource;
  externalRef: string | null;
  revision: number;
  status: CampaignAudienceStatus;
  exclusionReason: CampaignExclusionReason | null;
  nextEligibleAt: string | null;
  queuedAt: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  failedAt: string | null;
  createdAt: string;
  updatedAt: string;
  contact?: {
    firstName: string | null;
    lastName: string | null;
    businessName: string | null;
    optOut: boolean;
  };
  debt?: { invoiceNumber: string; balance: string; dueDate: string } | null;
}

export interface CampaignAudiencePage {
  items: CampaignAudienceMemberRecord[];
  total: number;
  eligibleCount: number;
  deferredCount: number;
  excludedCount: number;
  revision: number;
  audienceHash: string | null;
  frozenAt: string | null;
  mode: CampaignAudienceMode;
  nextCursor: string | null;
}

export interface AudienceMemberInput {
  contactId?: string;
  debtId?: string;
  externalRef?: string;
  phoneE164?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
}

export interface ReplaceCampaignAudienceRequest {
  expectedRevision: number;
  mode: CampaignAudienceMode;
  segmentFilter?: SegmentFilter;
  members?: AudienceMemberInput[];
}

export interface CampaignActivationPreview {
  previewId: string;
  audienceRevision: number;
  audienceHash: string;
  configHash: string;
  expiresAt: string;
  includedCount: number;
  eligibleCount: number;
  deferredCount: number;
  excludedCount: number;
  unreachableCount: number;
  unreachableSample: UnreachableAudienceMember[];
  reasonCounts: Partial<Record<CampaignExclusionReason, number>>;
  sample: CampaignAudienceMemberRecord[];
  firstMessage: string | null;
  schedule: {
    scheduleFrom: string;
    scheduleTo: string;
    timezone: string;
    maxContactsPerDay: number;
  };
  warnings: string[];
}

export interface ContactabilityPolicyRecord {
  id: string;
  tenantId: string;
  purpose: CampaignPurpose;
  channel: string;
  legalBasis: string;
  requiresConsent: boolean;
  policyVersion: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  evidenceRef: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
}

export interface CreateCampaignRequest {
  agentId?: string;
  whatsAppChannelId?: string;
  messagingChannelId?: string;
  messageTemplateId?: string;
  name: string;
  purpose?: CampaignPurpose;
  deliveryMode?: CampaignDeliveryMode;
  channelKind?: CampaignChannelKind;
  audienceMode?: CampaignAudienceMode;
  externalRef?: string;
  weekdayEnabled?: boolean;
  segmentFilter?: SegmentFilter;
  businessDays?: number[];
  scheduleKind?: CampaignScheduleKind;
  scheduleFrom?: string;
  scheduleTo?: string;
  afternoonFrom?: string | null;
  afternoonTo?: string | null;
  saturdayFrom?: string | null;
  saturdayTo?: string | null;
  saturdayAfternoonFrom?: string | null;
  saturdayAfternoonTo?: string | null;
  sundayFrom?: string | null;
  sundayTo?: string | null;
  sundayAfternoonFrom?: string | null;
  sundayAfternoonTo?: string | null;
  timezone?: string;
  holidays?: string[];
  initialTemplateId?: string;
  templateVariableMap?: CampaignTemplateVariableConfig;
  autoReplyText?: string | null;
  maxContactsPerDay?: number;
  steps?: Array<{ stepOrder: number; waitHours: number; templateId: string }>;
  endsAt: string;
}

export interface UpdateCampaignAudienceRequest {
  purpose: CampaignPurpose;
  segmentFilter?: SegmentFilter;
}

export const CAMPAIGN_AUDIENCE_LIMITS = {
  /** Lote de alta/reemplazo: mismo tope que sync Setup e import Excel (10k). */
  maxMembersPerRequest: 10000,
  /** Tope de campaña: alineado al import de cartera. */
  maxMembersPerCampaign: 10000,
  /** Archivo de audiencia: parseo en memoria; menor que cartera (10 MB). */
  maxImportFileBytes: 5 * 1024 * 1024,
  /** Filas de importacion de destinatarios / cartera. */
  maxImportRows: 10000,
  /** Pagina de listados de audiencia/contactos. */
  maxPageSize: 100,
  /** Sample de contactos sin dato de canal en preview de activacion. */
  maxUnreachableSample: 20,
  /** Preview de activacion vigente 15 min para revision humana. */
  previewTtlMs: 15 * 60 * 1000,
  /** Idempotencia HTTP: 24 h cubre reintentos de Commander. */
  idempotencyTtlMs: 24 * 60 * 60 * 1000,
  /** Payload de outbox sin PII; 8 KB alcanza referencias opacas. */
  maxOutboxPayloadBytes: 8192,
  /** Variables de plantilla / prueba: 20 cajas, 200 chars. */
  maxTemplateVariables: 20,
  maxTemplateVariableChars: 200,
  /** Lote de disparos de programa: no bloquear el API. */
  maxNoticeDispatchBatch: 100,
} as const;

export interface UnreachableAudienceMember {
  contactId: string;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
  phoneMasked: string;
  emailMasked: string | null;
  branch: string | null;
  exclusionReason: CampaignExclusionReason;
}

export interface AudiencePreviewRow {
  contactId?: string;
  name: string;
  /** Teléfono que usa la campaña (E.164) o el valor crudo del ERP si no es alcanzable. */
  phoneE164?: string;
  /** Email enmascarado para vista previa de campañas EMAIL. */
  email?: string;
  externalRef?: string;
  totalBalance: number;
  invoiceCount: number;
}

export interface AudienceReachabilityPreview {
  universeCount: number;
  afterFiltersCount: number;
  reachableCount: number;
  unreachableCount: number;
  deferredCount: number;
  excludedOtherCount: number;
  reasonCounts: Record<string, number>;
  reachablePreview?: AudiencePreviewRow[];
  missingPhonePreview?: AudiencePreviewRow[];
  missingEmailPreview?: AudiencePreviewRow[];
  missingPhoneCount?: number;
  reachableTotalBalance?: number;
  missingPhoneTotalBalance?: number;
}

export interface UnreachableAudiencePage {
  items: UnreachableAudienceMember[];
  total: number;
  nextCursor: string | null;
}

export interface CampaignReplyItem {
  id: string;
  receivedAt: string;
  body: string;
  contactMasked: string;
  kind: CampaignChannelKind;
}

export interface CampaignRepliesPage {
  items: CampaignReplyItem[];
  total: number;
  nextCursor: string | null;
}

export interface MessageTemplateRecord {
  id: string;
  tenantId: string;
  channelKind: CampaignChannelKind;
  name: string;
  subject: string | null;
  bodyText: string;
  bodyHtml: string | null;
  variableKeys: string[];
  status: MessageTemplateStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMessageTemplateRequest {
  channelKind: CampaignChannelKind;
  name: string;
  subject?: string;
  bodyText: string;
  bodyHtml?: string;
  variableKeys?: string[];
}

export interface UpdateMessageTemplateRequest {
  name?: string;
  subject?: string | null;
  bodyText?: string;
  bodyHtml?: string | null;
  variableKeys?: string[];
  status?: MessageTemplateStatus;
}

export interface ContactChannelOptOutRecord {
  contactId: string;
  channelKind: CampaignChannelKind;
  optedOutAt: string;
  reason: string | null;
}

export interface NoticeProgramRecord {
  id: string;
  tenantId: string;
  instanceId: string;
  instanceBotId: string | null;
  name: string;
  purpose: CampaignPurpose;
  deliveryMode: CampaignDeliveryMode;
  channelKind: CampaignChannelKind;
  whatsAppChannelId: string | null;
  messagingChannelId: string | null;
  messageTemplateId: string | null;
  frozenMessage: FrozenMessage | null;
  sendPolicy: NoticeSendPolicy;
  quietFrom: string | null;
  quietTo: string | null;
  maxPerContactPerDay: number;
  timezone: string;
  status: CampaignStatus;
  externalRef: string | null;
  createdAt: string;
  updatedAt: string;
  setupOrigin?: SetupEventOrigin | null;
}

export interface SetupEventOrigin {
  detectorId: SetupEventDetectorId;
  detectorLabel: string;
  connectionName: string;
}

export interface SetupEventBindingRecord {
  id: string;
  tenantId: string;
  connectionId: string;
  detectorId: SetupEventDetectorId;
  programId: string;
  enabled: boolean;
  cadenceMinutes: number;
  params: Record<string, unknown> | null;
  primed: boolean;
  watermarkAt: string | null;
  lastSweepStatus: PortfolioSyncStatus;
  lastSweepAt: string | null;
  lastSweepError: string | null;
  lastSweepStats: Record<string, unknown> | null;
  programName?: string;
  programStatus?: CampaignStatus;
}

export interface SetupEventDetectorView {
  detector: EventDetectorManifest;
  binding: SetupEventBindingRecord | null;
}

export interface UpsertSetupEventBindingRequest {
  enabled: boolean;
  programId: string;
  cadenceMinutes?: number;
  params?: Record<string, unknown>;
}

export interface CreateNoticeProgramRequest {
  name: string;
  purpose?: CampaignPurpose;
  channelKind: CampaignChannelKind;
  instanceId: string;
  instanceBotId?: string | null;
  messageTemplateId?: string | null;
  whatsAppChannelId?: string | null;
  whatsAppTemplateName?: string | null;
  /**
   * Que dato va en cada variable de la plantilla de WhatsApp, en orden ({{1}}, {{2}}, ...).
   * Si no se indica, se toma de la plantilla cuando fue creada en la plataforma.
   */
  whatsAppTemplateVariables?: string[] | null;
  sendPolicy: NoticeSendPolicy;
  quietFrom?: string | null;
  quietTo?: string | null;
  maxPerContactPerDay?: number;
  timezone?: string;
  externalRef?: string | null;
}

export interface UpdateNoticeProgramRequest {
  name?: string;
  purpose?: CampaignPurpose;
  channelKind?: CampaignChannelKind;
  instanceId?: string;
  instanceBotId?: string | null;
  messageTemplateId?: string | null;
  whatsAppChannelId?: string | null;
  whatsAppTemplateName?: string | null;
  whatsAppTemplateVariables?: string[] | null;
  sendPolicy?: NoticeSendPolicy;
  quietFrom?: string | null;
  quietTo?: string | null;
  maxPerContactPerDay?: number;
  timezone?: string;
  externalRef?: string | null;
}

export interface NoticeDispatchRecord {
  id: string;
  tenantId: string;
  programId: string;
  contactId: string | null;
  occurrenceId: string;
  status: CampaignAudienceStatus;
  exclusionReason: CampaignExclusionReason | null;
  providerId: string | null;
  queuedAt: string | null;
  sentAt: string | null;
  createdAt: string;
  maskedTo: string;
}

export interface NoticeUnreachablePage {
  items: NoticeDispatchRecord[];
  total: number;
}

export interface NoticeDispatchRequest {
  occurrenceId: string;
  recipient: {
    phoneE164?: string;
    email?: string;
    externalRef?: string;
    firstName?: string;
    lastName?: string;
  };
  variables?: Record<string, string>;
  sendAt?: string;
}

export interface NoticeDispatchResult {
  status: NoticeDispatchStatus;
  reason?: NoticeDispatchReason;
  dispatchId: string;
}
